import { FC, useContext, useEffect, useMemo, useState } from "react";
import { QESButton, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import ResourceTaggingService from "@app/resource-tagging/resourceTaggingService";
import { ManageResourceTagDisplayModel, Perspectives } from "@app/tags/types";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import QESOptionsList from "@app/shared/qes-standards/forms/OptionsList";
import { ProjectService } from "@app/projects";
import { ResourceStatus } from "@app/resources";
import { toast } from "react-toastify";

interface ProjectOption {
  name: string,
  id: string
}

const ResourceTagPanel: FC = () => {
  const flyoutContext = useContext(FlyoutContext);
  const [tagName, setTagName] = useState("");
  const [groupName, setGroupName] = useState("");
  const [projectId, setProjectId] = useState<string>("");
  const [tags, setTags] = useState<ManageResourceTagDisplayModel[]>([]);
  const [projectOptions, setProjectOptions] = useState<ProjectOption[]>([]);

  const resourceTaggingService = new ResourceTaggingService();
  const projectService = new ProjectService();

  const getOptions = async () => {
    try {
      const tags = await resourceTaggingService.getClientProjectTagInfo(Perspectives.All);
      setTags(tags);
      const projects = await projectService.getProjectResources(ResourceStatus.Active);
      setProjectOptions(projects.map(project => ({ id: project.resourceId + "", name: project.name })));
    } catch {
      toast("Problem loading groups and projects.",
        {
          containerId: "z-index-9999",
          type: "warning"
        }
      );
    }
  }

  useEffect(() => {
    getOptions();
  }, []);

  const tagOptions = useMemo(() => {
    const distinctTags = [...new Set(tags.map(tag => tag.tagName))];
    return distinctTags.map(tag => ({ key: tag, value: tag }));
  }, [tags]);

  const groupOptions = useMemo(() => {
    const distinctGroups = [...new Set(tags.map(tag => tag.tagGroupName))];
    return distinctGroups.map(group => ({ key: group, value: group }));
  }, [tags]);

  const isDuplicate = useMemo(() => {
    const project = projectOptions.find(p => p.id === projectId);
    const tag = tags.find(t => t.tagName === tagName && t.tagGroupName === groupName && t.projectName === project?.name);
    return !!tag;
  }, [projectId, tagName, groupName, tags, projectOptions]);

  const handleSubmit = async () => {
    try {
      const project = projectOptions.find(p => p.id === projectId);
      if (!project) {
        toast("Unable to find project.",
          {
            containerId: "z-index-9999",
            type: "warning"
          }
        );
        return;
      }
      const tag = {
        customProjectId: parseInt(project.id),
        projectName: project.name,
        tagGroupName: groupName,
        tagName: tagName
      }
      await projectService.createProjectTagsAsync([tag])
      toast("Tag created.",
        {
          containerId: "z-index-9999",
          type: "success"
        }
      );
      flyoutContext.handleCloseFlyout();
    } catch {
      toast("Something went wrong.",
        {
          containerId: "z-index-9999",
          type: "warning"
        }
      );
    }
  }

  return (
    <>
      <div className="row mt-3">
        <div className="col-8">
          <QESSelect
            label="Project"
            testId={`resource-tag-panel__project`}
            value={projectId}
            isValid={!!projectId}
            required={true}
            onChange={(e) => setProjectId(e.target.value)}
          >
            {projectOptions.map(project => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </QESSelect>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-8">
          <QESTextInput label={"Group Name"}
                        testId="resource-tag-panel__group-name"
                        value={groupName}
                        required={true}
                        isValid={!!groupName}
                        placeholder="Select or create a group"
                        optionsList={<QESOptionsList options={groupOptions} textIfNoMatch="No groups found."/>}
                        onChange={e => setGroupName(e.target.value)}
          >
          </QESTextInput>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-8">
          <QESTextInput label="Tag Name"
                        testId="resource-tag-panel__tag-name"
                        value={tagName}
                        isValid={!!tagName}
                        required={true}
                        placeholder="Select or create a tag"
                        optionsList={<QESOptionsList options={tagOptions} textIfNoMatch="No tags found."/>}
                        onChange={(e) => setTagName(e.target.value)}/>
        </div>
      </div>
      <FlyoutFooter submitButton={<QESButton qesTestId="resource-tag-panel__submit" onClick={handleSubmit}
                                             disabled={!tagName || !groupName || !projectId || isDuplicate}>Save</QESButton>}/>
    </>
  );
}

export default ResourceTagPanel;